import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, FileText, Loader2, Save, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DynamicField } from "./DynamicField";
import { playSuccessSound } from "@/utils/sound";
import { notifyIncidentChange } from "@/utils/incidentEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const apiError = error => {
  const detail = error.response?.data?.detail;
  return typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join("; ") : "Die Daten konnten nicht geladen oder gespeichert werden.";
};
const defaultsFor = (form, initial) => {
  const previous = { ...(initial || {}), ...(initial?.field_values || {}) };
  return Object.fromEntries(form.fields.map(f => [f.field_key, previous[f.field_key] ?? f.default_value ??
    (["boolean", "checkbox"].includes(f.field_type) ? false : ["multiselect", "image", "file"].includes(f.field_type) ? [] : f.field_type === "date" ? new Date().toLocaleDateString("sv-SE") : "")]));
};

export const IncidentWorkflow = ({ incidentId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [initial, setInitial] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [booting, setBooting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const companyId = incidentId ? initial?.company_id : params.get("unternehmen") || user?.company_id;
  const typeId = incidentId ? initial?.meldungsart_id || initial?.meldungsart : params.get("art");

  useEffect(() => {
    let active = true;
    setBooting(true); setError("");
    (async () => {
      try {
        const { data: me } = await axios.get(`${API}/auth/me`);
        if (!active) return;
        setUser(me);
        if (incidentId) {
          const { data } = await axios.get(`${API}/incidents/${incidentId}`);
          if (active) setInitial(data);
        } else if (!me.company_id && me.is_admin) {
          const { data } = await axios.get(`${API}/companies`);
          if (active) setCompanies(data);
        }
      } catch (e) { if (active) setError(apiError(e)); }
      finally { if (active) setBooting(false); }
    })();
    return () => { active = false; };
  }, [incidentId]);

  useEffect(() => {
    if (!companyId || typeId || booting) return;
    let active = true;
    setCatalog(null); setLoading(true); setError("");
    axios.get(`${API}/form-runtime`, { params: { company_id: companyId } }).then(({ data }) => {
      if (active) setCatalog(data);
    }).catch(e => { if (active) setError(apiError(e)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [companyId, typeId, location.key, booting, reload]);

  useEffect(() => {
    setForm(null);
    if (!companyId || !typeId || booting) return;
    let active = true;
    setLoading(true); setError("");
    axios.get(`${API}/form-runtime/${encodeURIComponent(typeId)}`, { params: { company_id: companyId, ...(incidentId ? { incident_id: incidentId } : {}) } }).then(({ data }) => {
      if (active) { setForm(data); setValues(defaultsFor(data, initial)); }
    }).catch(e => { if (active) setError(apiError(e)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [companyId, typeId, incidentId, initial, booting, reload]);

  const chooseCompany = id => setParams({ unternehmen: id });
  const chooseType = id => setParams({ unternehmen: companyId, art: id });
  const goBack = () => {
    if (Object.values(values).some(v => v && (!Array.isArray(v) || v.length)) && form && !window.confirm("Eingaben verwerfen und Meldungsart wechseln?")) return;
    setParams(companyId ? { unternehmen: companyId } : {});
    setError("");
  };
  const submit = async event => {
    event.preventDefault();
    if (!form || saving || uploading) return;
    for (const field of form.fields.filter(f => f.is_visible !== false && f.is_required)) {
      const value = values[field.field_key];
      if (value == null || (typeof value === "string" && !value.trim()) || (Array.isArray(value) && !value.length)) {
        setError(`Bitte füllen Sie "${field.label}" aus`);
        document.getElementById(`incident-field-${field.field_key}`)?.focus();
        return;
      }
    }
    setSaving(true); setError("");
    try {
      const payload = { company_id: form.company_id, meldungsart_id: form.meldungsart_id, meldungsart: form.meldungsart_name, schema_id: form.id, schema_version: form.version, field_values: values };
      const { data } = incidentId ? await axios.put(`${API}/incidents/${incidentId}`, payload) : await axios.post(`${API}/incidents`, payload);
      notifyIncidentChange();
      playSuccessSound(); toast.success(incidentId ? "Meldung aktualisiert" : "Meldung gespeichert");
      navigate(`/unfall/${data.id}`);
    } catch (e) { setError(apiError(e)); }
    finally { setSaving(false); }
  };

  if (booting) return <div data-testid="incident-initial-loading" role="status" className="py-16"><Loader2 className="animate-spin text-yellow-500" /></div>;
  return <div className="max-w-4xl mx-auto w-full min-w-0 py-4 sm:py-8" data-testid={incidentId ? "edit-incident-page" : "new-incident-page"}>
    <header className="mb-8 space-y-3">
      <p className="text-yellow-500 text-xs font-semibold uppercase" data-testid="incident-company-name">{form?.company_name || catalog?.company_name || user?.company_name || "Unternehmen auswählen"}</p>
      <h1 data-testid="incident-page-title" className="text-3xl sm:text-4xl font-bold break-words" style={{ fontFamily: "Chivo, sans-serif" }}>{incidentId ? "Meldung bearbeiten" : "Neue Meldung"}</h1>
    </header>
    {!incidentId && !user?.company_id && <div className="mb-8 max-w-md"><label htmlFor="incident-company" className="block mb-2 text-sm text-zinc-400">Unternehmen</label><Select value={companyId || ""} onValueChange={chooseCompany}><SelectTrigger id="incident-company" data-testid="incident-company-select" className="bg-zinc-950 border-zinc-700"><SelectValue placeholder="Unternehmen auswählen" /></SelectTrigger><SelectContent className="bg-zinc-900 border-zinc-700">{companies.map(c => <SelectItem key={c.id} data-testid={`incident-company-${c.id}`} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>}
    {error && <div role="alert" data-testid="incident-form-error" className="border-l-2 border-red-500 bg-red-500/10 p-4 mb-6 text-red-300 break-words">
      {error}
      <Button type="button" variant="ghost" data-testid="incident-form-retry" className="mt-2 block" onClick={() => { if (!form || window.confirm("Formular neu laden und Eingaben verwerfen?")) setReload(n => n + 1); }}><RefreshCw size={14} className="inline mr-2" />Erneut laden</Button>
    </div>}
    {loading && <div data-testid="incident-form-loading" role="status" className="flex gap-3 items-center py-12 text-zinc-400"><Loader2 size={20} className="animate-spin text-yellow-500" />Formular wird geladen…</div>}
    {!typeId && !loading && catalog && <section data-testid="incident-type-selection" className="space-y-6">
      <h2 className="text-lg font-semibold" data-testid="incident-type-question">Welche Meldung möchtest du erstellen?</h2>
      <div className="grid gap-4 sm:grid-cols-2">{catalog.meldungsarten.map(type => <button type="button" key={type.id} data-testid={`select-incident-type-${type.id}`} onClick={() => chooseType(type.id)} className="group flex items-start gap-4 text-left p-5 sm:p-6 border border-zinc-700 bg-zinc-900/50 rounded-md hover:border-yellow-500 hover:bg-yellow-500/5 focus-visible:outline-yellow-500 transition-colors min-w-0">
        <FileText size={24} className="text-yellow-500 shrink-0 mt-1" /><span className="flex-1 min-w-0"><span className="block font-semibold break-words">{type.name}</span>{type.description && <span className="block mt-2 text-sm text-zinc-400 break-words">{type.description}</span>}</span><ArrowRight size={18} className="shrink-0 text-zinc-500 group-hover:text-yellow-500 mt-1" />
      </button>)}</div>
      {!catalog.meldungsarten.length && <p data-testid="incident-types-empty" className="text-zinc-400 border-l-2 border-yellow-500 pl-4">Für dieses Unternehmen sind keine veröffentlichten Meldungsformulare aktiv.</p>}
    </section>}
    {!loading && typeId && !form && !incidentId && <Button data-testid="incident-selection-return" variant="outline" onClick={() => setParams({ unternehmen: companyId })}><ArrowLeft size={16} className="mr-2" />Zur Auswahl</Button>}
    {!loading && form && <form onSubmit={submit} noValidate data-testid="incident-runtime-form" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 pb-6">
        <div><h2 data-testid="incident-selected-type" className="text-lg font-semibold break-words">{form.meldungsart_name}</h2><p data-testid="incident-schema-version" className="text-xs text-zinc-500 mt-1">Formularversion {form.version}</p></div>
        {!incidentId && <Button data-testid="incident-change-type" type="button" variant="outline" disabled={saving || uploading} onClick={goBack}><ArrowLeft size={16} className="mr-2" />Meldungsart ändern</Button>}
      </div>
      <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2">
        {form.fields.map(field => <DynamicField key={field.id} field={field} value={values[field.field_key]} historicalValue={initial?.field_values?.[field.field_key] ?? initial?.[field.field_key]} onChange={value => setValues(v => ({ ...v, [field.field_key]: value }))} onBusy={setUploading} onError={setError} disabled={saving || uploading} />)}
      </div>
      <div className="flex flex-wrap justify-end gap-3 border-t border-zinc-800 pt-6">
        <Button data-testid="incident-cancel" type="button" variant="outline" disabled={saving || uploading} onClick={() => navigate(incidentId ? `/unfall/${incidentId}` : "/unfaelle")}>Abbrechen</Button>
        <Button data-testid="incident-submit" type="submit" disabled={saving || uploading} className="bg-yellow-500 hover:bg-yellow-400 text-black">{saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}{saving ? "Wird gespeichert…" : "Meldung speichern"}</Button>
      </div>
    </form>}
  </div>;
};